/**
 * Bake a model built from many meshes into one vertex-coloured geometry.
 *
 * The drivable car and bike are dozens of meshes each: fine for the one being
 * driven, far too many draw calls for a car park. Baked, a model becomes one
 * geometry with each part's colour in its vertices. That can be drawn as an
 * instance among the props, a whole tile's cars of a kind in one call.
 *
 * Parts whose material is in `tinted` are painted white and flagged in
 * `aTint`, so an instance colour paints them (the body) and nothing else (not
 * the tyres or the glass). Interior parts (`userData.interior`) are left out,
 * since nobody looks into a parked car.
 */

import type { BufferGeometry, Material, Object3D } from 'three';
import { BufferAttribute, Color, InstancedMesh, Matrix4, Mesh, MeshStandardMaterial } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const scratch = new Matrix4();
const instance = new Matrix4();

export function bakeModel(root: Object3D, tinted: ReadonlySet<Material>): BufferGeometry {
  root.updateMatrixWorld(true);
  const parts: BufferGeometry[] = [];
  const colour = new Color();
  // Only what shows: not a hidden part such as a car's shut driver's door.
  root.traverseVisible((object) => {
    if (!(object instanceof Mesh) || object.userData.interior === true) return;
    const material = object.material as Material;
    if (!(material instanceof MeshStandardMaterial)) return;
    const tint = tinted.has(material);
    colour.copy(tint ? new Color(1, 1, 1) : material.color);
    // A glass that is see-through when driven is a dark pane when parked.
    if (material.transparent) colour.multiplyScalar(0.7);
    const copies = object instanceof InstancedMesh ? object.count : 1;
    for (let c = 0; c < copies; c++) {
      const source = object.geometry as BufferGeometry;
      const flat = source.index === null ? source.clone() : source.toNonIndexed();
      for (const name of Object.keys(flat.attributes)) if (name !== 'position') flat.deleteAttribute(name);
      if (object instanceof InstancedMesh) {
        object.getMatrixAt(c, instance);
        flat.applyMatrix4(scratch.multiplyMatrices(object.matrixWorld, instance));
      } else {
        flat.applyMatrix4(object.matrixWorld);
      }
      flat.computeVertexNormals();
      const count = flat.getAttribute('position').count;
      const colours = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) colours.set([colour.r, colour.g, colour.b], i * 3);
      flat.setAttribute('color', new BufferAttribute(colours, 3));
      flat.setAttribute('aGlow', new BufferAttribute(new Float32Array(count), 1));
      flat.setAttribute('aTint', new BufferAttribute(new Float32Array(count).fill(tint ? 1 : 0), 1));
      parts.push(flat);
    }
  });
  const merged = mergeGeometries(parts);
  parts.forEach(p => p.dispose());
  return merged;
}
